import Button from "components/shared/button";
import { Popup } from "components/popups/Popup";
import React, { useEffect, useState } from "react";
import Input from "components/shared/inputs/input";
import useTranslation from "utils/hooks/useTranslation";
import useCustomForm from "utils/hooks/useCustomForm";
import { useCreateLocaleMutation, useUpdateLocaleMutation } from "store/api";
import type { DatasourceMutationRequest } from "./TvaPopup";

type EditableLocaleRow = {
  id?: number | string;
  code?: string;
  value?: string;
  french?: string;
};

type LocalePopupProps = {
  isOpen: boolean;
  onClose: () => void;
  request: DatasourceMutationRequest | null;
  lang: string;
  mode?: "create" | "edit";
  row?: EditableLocaleRow | null;
  onSaved?: () => Promise<void> | void;
};

const LocalePopup: React.FC<LocalePopupProps> = ({
  isOpen,
  onClose,
  request,
  lang,
  mode = "edit",
  row,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [createLocale] = useCreateLocaleMutation();
  const [updateLocale] = useUpdateLocaleMutation();
  const isEditMode = mode === "edit";

  const { control, reset, getValues } = useCustomForm<{
    code: string;
    value: string;
  }>({
    defaultValues: { code: "", value: "" },
  });

  useEffect(() => {
    setIsSaving(false);
    reset({
      code: isOpen ? String(row?.code ?? "") : "",
      value: isOpen ? String(row?.value ?? "") : "",
    });
  }, [isOpen, reset, row?.code, row?.value]);

  const handleSave = async () => {
    if (!request) return;
    const rowId = row?.id ?? (row as any)?.key;
    if (isEditMode && rowId == null) return;

    const { code, value } = getValues();
    const baseUrl = request.url.replace(/\/[^/]+$/, '');
    const url = isEditMode ? `${baseUrl}/${rowId}` : baseUrl;

    setIsSaving(true);
    try {
      const payload = {
        id: rowId,
        code,
        value,
        french: row?.french,
        lang,
      };

      if (isEditMode) {
        await updateLocale({ url, data: payload }).unwrap();
      } else {
        await createLocale({ url, data: payload }).unwrap();
      }

      onSaved?.();
      onClose();
    } catch {
      // handled by api interceptor
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Popup
      title={isEditMode ? "Modifier locale" : "Nouvelle locale"}
      onClose={onClose}
      portal
      fixSize="middle"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button type="button" primary onClick={handleSave} disabled={isSaving}>
              {t(isEditMode ? "Update" : "Add")}
            </Button>
          </group>
        ),
      }}
    >
      <group
        data-direction="column"
        data-gap="20"
        data-space="40"
        data-space-horizontal="40"
        data-space-top="5"
        data-height="autofit"
      >
        <group data-gap="10" data-space-vertical="20">
          <group data-direction="column" data-gap="20">
            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"Code"}
                  name="code"
                  control={control}
                  type="text"
                  size="large"
                />
              </group>
            </group>

            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"French Label"}
                  name="french_preview"
                  defaultValue={String(row?.french ?? "")}
                  disabled
                  size="large"
                />
              </group>
            </group>

            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"Value"}
                  name="value"
                  control={control}
                  type="textarea"
                  size="large"
                />
              </group>
            </group>
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default LocalePopup;
